// src/utils/consoleLogger.js
// Forwards console logs to Firestore for debugging
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

// Configuration
const CONFIG = {
  enabled: true,                    // Master switch
  maxMessageLength: 5000,           // Truncate long messages
  batchSize: 10,                    // Batch writes for performance
  batchDelayMs: 1000,               // Flush batch after this delay
  levels: ['error', 'warn', 'log'], // Which levels to capture ('log' can be noisy)
  captureStackTrace: true,          // Include stack traces for errors
};

// Batch buffer
let logBuffer = [];
let flushTimeout = null;

// Store original console methods
const originalConsole = {
  log: console.log.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
  info: console.info.bind(console),
};

// Format arguments for storage
function formatArgs(args) {
  return args.map(arg => {
    if (arg === null) return 'null';
    if (arg === undefined) return 'undefined';
    if (arg instanceof Error) {
      return `${arg.name}: ${arg.message}\n${arg.stack || ''}`;
    }
    if (typeof arg === 'object') {
      try {
        return JSON.stringify(arg, null, 2);
      } catch (e) {
        return '[Circular or non-serializable object]';
      }
    }
    return String(arg);
  }).join(' ');
}

// Get current user ID safely
function getCurrentUserId() {
  try {
    const auth = getAuth();
    return auth.currentUser?.uid || 'anonymous';
  } catch {
    return 'anonymous';
  }
}

// Flush buffered logs to Firestore
async function flushLogs() {
  if (logBuffer.length === 0) return;

  const logsToWrite = [...logBuffer];
  logBuffer = [];
  flushTimeout = null;

  const userId = getCurrentUserId();
  // Still write logs even for anonymous users - store under 'anonymous' collection
  // This helps debug login/auth issues

  try {
    const logsRef = collection(db, 'users', userId, 'consoleLogs');

    // Write each log (could batch with writeBatch for better performance)
    for (const log of logsToWrite) {
      await addDoc(logsRef, {
        ...log,
        userId,
        createdAt: serverTimestamp(),
      });
    }
  } catch (e) {
    // Use original console to avoid infinite loop
    originalConsole.error('[ConsoleLogger] Failed to write logs:', e);
  }
}

// Schedule a flush
function scheduleFlush() {
  if (flushTimeout) return;
  flushTimeout = setTimeout(flushLogs, CONFIG.batchDelayMs);
}

// Add log to buffer
function bufferLog(level, args) {
  const message = formatArgs(args);

  // Get stack trace for context
  let stack = null;
  if (CONFIG.captureStackTrace && (level === 'error' || level === 'warn')) {
    try {
      const err = new Error();
      stack = err.stack?.split('\n').slice(3, 8).join('\n'); // Skip internal frames
    } catch {}
  }

  logBuffer.push({
    level,
    message: message.slice(0, CONFIG.maxMessageLength),
    url: window.location.href,
    pathname: window.location.pathname,
    timestamp: new Date().toISOString(),
    stack,
    userAgent: navigator.userAgent,
  });

  // Flush immediately for errors, batch for others
  if (level === 'error' || logBuffer.length >= CONFIG.batchSize) {
    flushLogs();
  } else {
    scheduleFlush();
  }
}

// Create interceptor for a console method
function createInterceptor(level) {
  return function(...args) {
    // Always call original
    originalConsole[level](...args);

    // Buffer for Firestore if enabled and level is tracked
    if (CONFIG.enabled && CONFIG.levels.includes(level)) {
      bufferLog(level, args);
    }
  };
}

// Initialize console interception
export function initConsoleLogger(options = {}) {
  // Merge options
  Object.assign(CONFIG, options);

  if (!CONFIG.enabled) {
    originalConsole.log('[ConsoleLogger] Disabled');
    return;
  }

  // Replace console methods
  console.log = createInterceptor('log');
  console.warn = createInterceptor('warn');
  console.error = createInterceptor('error');
  console.info = createInterceptor('log'); // Treat info as log

  // Capture unhandled errors
  window.addEventListener('error', (event) => {
    bufferLog('error', [
      `Unhandled Error: ${event.message}`,
      `at ${event.filename}:${event.lineno}:${event.colno}`,
      event.error?.stack || ''
    ]);
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    bufferLog('error', [
      'Unhandled Promise Rejection:',
      event.reason?.message || event.reason || 'Unknown reason',
      event.reason?.stack || ''
    ]);
  });

  // Flush on page unload
  window.addEventListener('beforeunload', () => {
    flushLogs();
  });

  originalConsole.log('[ConsoleLogger] Initialized - forwarding to Firestore');
}

// Utility to manually log something important
export function logToFirestore(level, message, metadata = {}) {
  bufferLog(level, [message, metadata]);
}

// Restore original console (for cleanup)
export function restoreConsole() {
  console.log = originalConsole.log;
  console.warn = originalConsole.warn;
  console.error = originalConsole.error;
  console.info = originalConsole.info;
}

// Export config for runtime changes
export { CONFIG as consoleLoggerConfig };
